/**
 * The customer map: every site a customer operates on one map, with the whole
 * view — which group, which statuses, alarm categories and load types, the
 * search text, the ordering and the viewport — carried in the URL so
 * a map can be shared as a link and reload lands where it left off.
 *
 * The page owns the data: it fetches the customer's sites with per-site detail
 * — refetching only when a narrowing the endpoint owns changes — and does all
 * other filtering, searching and sorting on the client, so panning and toggling
 * never waits on the network. Beside the map sits one sidebar with two modes —
 * the site browser, and the detail panel for whichever site is selected —
 * because selecting a site moves the URL to that site rather than leaving the
 * map. A link that names a site opens centred on it.
 *
 * On a phone there is room for one of the two at a time, so a segmented control
 * swaps map for list and the detail panel covers the stage as a sheet.
 */
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useScope } from '@sophocles/app';
import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import SiteMap from '@sophocles/components/map/SiteMap';
import { useScopedPreferenceKey } from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import { fetchCustomerMapSites } from '@sophocles/features/map/api';
import { filterSites, searchSites, sortSites, statusFiltersFromSelection } from '@sophocles/features/map/mapState';
import useMapUrlState from '@sophocles/features/map/useMapUrlState';
import useSiteTerm from '@sophocles/hooks/useSiteTerm';
import useUserPreference from '@sophocles/hooks/useUserPreference';
import { customerMapPath, customerMapSitePath, siteGroupMapPath, siteGroupMapSitePath } from '@sophocles/router/paths';
import MapFilterBar from '@sophocles/views/map/components/MapFilterBar';
import MapSidebar from '@sophocles/views/map/components/MapSidebar';
import MapSiteDetailPanel from '@sophocles/views/map/components/MapSiteDetailPanel';
import {
  DEFAULT_SHOWN_FIELDS,
  knownFields,
  PREFERENCE_BASE_KEY,
  PREFERENCE_COLUMNS_SUFFIX,
} from '@sophocles/views/map/mapFields';

const SIDEBAR_WIDTH = 400;
const MOBILE_PANE = Object.freeze({ MAP: 'map', LIST: 'list' });

function CustomerMapView(props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customer, siteGroup } = useScope();
  const { site_id: selectedSiteParam } = useParams();
  const { search } = useLocation();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));
  const siteTerm = useSiteTerm();
  const customerId = customer?.id;
  const isBillboard = Boolean(customer?.is_billboard);

  // Under a plant route the group is the page, not a filter: it comes from the
  // URL's own scope, so the bar offers no group control, "clear all" cannot
  // reach it, and the query string never carries it. Rows drop the group name
  // too — every one of them belongs to the same group.
  const scopedGroupId = siteGroup?.id ?? null;
  const isGroupScoped = scopedGroupId != null;

  const { state, setState } = useMapUrlState(customerId);
  const selectedSiteId = selectedSiteParam == null ? null : Number(selectedSiteParam);

  // A link that names a site opens centred on it rather than fitting the whole
  // customer — unless it also carries a viewport, which the sender chose
  // deliberately. Read once, at mount: this decides how the map opens, and a
  // later selection flies on its own without re-framing the whole set.
  const deepLinkSiteIdRef = useRef(selectedSiteId != null && state.bbox == null ? selectedSiteId : null);
  const [isDeepLinkSiteMissing, setIsDeepLinkSiteMissing] = useState(false);

  // Data — fetch every site the customer operates (with detail) whenever the
  // customer or the group narrowing changes; a request-id + AbortController
  // guard drops stale responses.
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fitKey, setFitKey] = useState(0);
  const requestIdRef = useRef(0);

  // The group and load-type narrowings are the endpoint's, not the client's, so
  // changing either refetches. A joined key keeps the dependency a primitive.
  const loadTypeKey = state.lt.join(',');
  const groupId = scopedGroupId ?? state.group;
  useEffect(() => {
    if (!customerId) return undefined;
    const myId = ++requestIdRef.current;
    const controller = new AbortController();
    const loadTypes = loadTypeKey ? loadTypeKey.split(',') : [];
    setLoading(true);
    fetchCustomerMapSites({ customerId, siteGroupId: groupId, loadTypes }, { signal: controller.signal })
      .then(data => {
        if (requestIdRef.current !== myId) return;
        setSites(Array.isArray(data?.sites) ? data.sites : []);
        setError(null);
        // A pending deep link centres the map itself; fitting the whole set
        // first would yank the view away from the site that was asked for.
        if (deepLinkSiteIdRef.current == null) setFitKey(key => key + 1);
        setLoading(false);
      })
      .catch(err => {
        if (requestIdRef.current !== myId) return;
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        setError(err?.response?.data?.detail || err?.message || t('map.noDataFound', 'No Data Found'));
        setLoading(false);
      });
    return () => controller.abort();
  }, [customerId, groupId, loadTypeKey, t]);

  const visibleSites = useMemo(() => {
    const byStatus = filterSites(sites, {
      statusFilters: statusFiltersFromSelection(state.status),
      selectedCategories: state.cat,
    });
    return sortSites(searchSites(byStatus, state.q), state.sort);
  }, [sites, state.status, state.cat, state.q, state.sort]);

  const [hoveredSiteId, setHoveredSiteId] = useState(null);
  const [mobilePane, setMobilePane] = useState(MOBILE_PANE.MAP);
  const [pendingLocateSiteId, setPendingLocateSiteId] = useState(null);
  const mapApiRef = useRef(null);

  // Configurable row details. The saved preference is the whole state — the
  // configure dialog commits on save, so there is no unsaved selection to hold.
  const preferenceKey = useScopedPreferenceKey(PREFERENCE_BASE_KEY, PREFERENCE_COLUMNS_SUFFIX);
  const [savedShownFields, setSavedShownFields] = useUserPreference(preferenceKey, null);
  const shownFields = useMemo(
    () => (savedShownFields ? knownFields(savedShownFields) : [...DEFAULT_SHOWN_FIELDS]),
    [savedShownFields],
  );

  // Centre a deep link once its dataset lands. A site the filters exclude is
  // reported rather than silently ignored — the map is not broken, it is
  // narrowed, and only the user can decide to widen it.
  useEffect(() => {
    const pendingSiteId = deepLinkSiteIdRef.current;
    if (pendingSiteId == null || sites.length === 0) return;
    deepLinkSiteIdRef.current = null;
    if (sites.some(site => site.id === pendingSiteId)) setPendingLocateSiteId(pendingSiteId);
    else setIsDeepLinkSiteMissing(true);
  }, [sites]);

  // Locating waits for a map to fly: on a phone the canvas may still be behind
  // the list pane when the row is clicked.
  useEffect(() => {
    if (pendingLocateSiteId == null) return;
    if (isMobile && mobilePane !== MOBILE_PANE.MAP) return;
    mapApiRef.current?.findOnMap(pendingLocateSiteId);
    setPendingLocateSiteId(null);
  }, [pendingLocateSiteId, isMobile, mobilePane]);

  // Selecting keeps the current filters — only the site segment moves — and
  // flies the map to the site, because a row or marker click is a reader saying
  // "this one", and leaving the canvas where it was makes them find it twice.
  // On a phone the flight waits: selecting opens the detail sheet over the
  // stage, so the pending id is spent the next time the map pane is shown.
  const handleSelectSite = useCallback(
    site => {
      const siteId = site == null ? null : (site.site_id ?? site.id);
      const params = { cust_id: customerId, sitegroup_id: scopedGroupId, site_id: siteId };
      let target;
      if (isGroupScoped) target = siteId == null ? siteGroupMapPath(params) : siteGroupMapSitePath(params);
      else target = siteId == null ? customerMapPath(params) : customerMapSitePath(params);
      navigate(`${target}${search}`);
      if (siteId != null) setPendingLocateSiteId(siteId);
    },
    [navigate, customerId, scopedGroupId, isGroupScoped, search],
  );

  const handleBack = useCallback(() => handleSelectSite(null), [handleSelectSite]);

  const handleFindOnMap = useCallback(
    item => {
      if (isMobile) setMobilePane(MOBILE_PANE.MAP);
      setPendingLocateSiteId(item.site_id ?? item.id);
    },
    [isMobile],
  );

  const handleBoundsChange = useCallback(bbox => setState({ bbox }), [setState]);
  const handleSortChange = useCallback(sort => setState({ sort }), [setState]);
  const handleSearchChange = useCallback(q => setState({ q }), [setState]);

  const mapPane = (
    <Box sx={{ position: 'relative', flex: 1, minWidth: 0, height: '100%' }}>
      <SiteMap
        ref={mapApiRef}
        sites={visibleSites}
        isBillboard={isBillboard}
        hoveredSiteId={hoveredSiteId}
        selectedSiteId={selectedSiteId}
        onHoverSite={setHoveredSiteId}
        onSelectSite={handleSelectSite}
        fitKey={fitKey}
        initialBounds={state.bbox}
        onBoundsChange={handleBoundsChange}
      />
    </Box>
  );

  const siteList = (
    <MapSidebar
      rows={visibleSites}
      totalCount={sites.length}
      title={siteTerm.plural}
      sort={state.sort}
      onSortChange={handleSortChange}
      hoveredSiteId={hoveredSiteId}
      selectedSiteId={selectedSiteId}
      onHoverSite={setHoveredSiteId}
      onSelectSite={handleSelectSite}
      onFindOnMap={handleFindOnMap}
      shownFields={shownFields}
      onShownFieldsChange={setSavedShownFields}
      showGroupName={!isGroupScoped && state.group == null}
    />
  );

  // The panel is meaningless without a site, so it only exists on the detail
  // route; the list route renders the sidebar list instead.
  const siteDetail =
    selectedSiteId == null ? null : (
      <MapSiteDetailPanel
        siteId={selectedSiteId}
        customerId={customerId}
        onBack={handleBack}
        onFindOnMap={setPendingLocateSiteId}
      />
    );

  return (
    <AppNav
      flex
      notFullHeight
      page={isGroupScoped ? 'plant_map' : 'customer_map'}
      loading={loading && sites.length === 0}
      {...props}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflow: 'hidden',
          padding: '0 var(--page-px) var(--page-py)',
        }}
      >
        <TitleBar title={t('map.map', 'Map')} />

        <Box sx={{ pb: 1 }}>
          <MapFilterBar
            state={state}
            setState={setState}
            customerId={customerId}
            isBillboard={isBillboard}
            sites={sites}
            matchCount={visibleSites.length}
            onSearchChange={handleSearchChange}
            showGroupFilter={!isGroupScoped}
          />
        </Box>

        {isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 1 }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={mobilePane}
              onChange={(_event, value) => value && setMobilePane(value)}
            >
              <ToggleButton value={MOBILE_PANE.MAP}>{t('map.showMap', 'Show Map')}</ToggleButton>
              <ToggleButton value={MOBILE_PANE.LIST}>{t('map.showList', 'Show List')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {String(error)}
          </Alert>
        )}

        {isDeepLinkSiteMissing && (
          <Alert severity="info" sx={{ mb: 1 }} onClose={() => setIsDeepLinkSiteMissing(false)}>
            {t('map.linkedSiteFilteredOut', 'The linked site is not in the current filters — clear them to see it.')}
          </Alert>
        )}

        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flex: 1,
            minHeight: 0,
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid var(--border-default)',
          }}
        >
          {(!isMobile || mobilePane === MOBILE_PANE.MAP) && mapPane}

          {isMobile ? (
            <>
              {mobilePane === MOBILE_PANE.LIST && <Box sx={{ flex: 1, minWidth: 0 }}>{siteList}</Box>}
              {selectedSiteId != null && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 1200,
                    bgcolor: 'background.paper',
                    overflowY: 'auto',
                  }}
                >
                  {siteDetail}
                </Box>
              )}
            </>
          ) : (
            <Box
              sx={{
                width: SIDEBAR_WIDTH,
                flexShrink: 0,
                height: '100%',
                overflowY: selectedSiteId == null ? 'hidden' : 'auto',
                borderLeft: '1px solid var(--border-default)',
                bgcolor: 'background.paper',
              }}
            >
              {selectedSiteId == null ? siteList : siteDetail}
            </Box>
          )}
        </Box>
      </Box>
    </AppNav>
  );
}

export default CustomerMapView;
